// Validação de CPF e CNPJ (inclusive o CNPJ alfanumérico da IN RFB nº 2.229/2024).
// Mesmo algoritmo do ValidadorDocumento do PreBilling.Common, para o front e a API
// aceitarem e recusarem os mesmos documentos.

const PESOS_CPF_PRIMEIRO_DIGITO = [10, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_CPF_SEGUNDO_DIGITO = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_CNPJ_PRIMEIRO_DIGITO = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_CNPJ_SEGUNDO_DIGITO = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

// Remove a máscara mantendo letras e dígitos, em maiúsculas.
// Ex.: "12.abc.345/01de-35" -> "12ABC34501DE35"
export const sanitizeDocumento = (value: string) => (value ? value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase() : "");

// Cada caractere vale seu código ASCII menos 48: '0'-'9' valem 0-9 e 'A'-'Z' valem 17-42.
const calculaDigito = (documento: string, pesos: number[]) => {
    const soma = pesos.reduce((total, peso, i) => total + (documento.charCodeAt(i) - 48) * peso, 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
};

const todosIguais = (documento: string) => documento.split("").every((c) => c === documento[0]);

export const isCpfValido = (cpf: string) => {
    const limpo = sanitizeDocumento(cpf);

    if (!/^\d{11}$/.test(limpo) || todosIguais(limpo)) return false;

    return (
        Number(limpo[9]) === calculaDigito(limpo, PESOS_CPF_PRIMEIRO_DIGITO) &&
        Number(limpo[10]) === calculaDigito(limpo, PESOS_CPF_SEGUNDO_DIGITO)
    );
};

// As 12 primeiras posições aceitam letras e dígitos; os 2 dígitos verificadores são sempre numéricos.
export const isCnpjValido = (cnpj: string) => {
    const limpo = sanitizeDocumento(cnpj);

    if (!/^[A-Z0-9]{12}\d{2}$/.test(limpo) || todosIguais(limpo)) return false;

    return (
        Number(limpo[12]) === calculaDigito(limpo, PESOS_CNPJ_PRIMEIRO_DIGITO) &&
        Number(limpo[13]) === calculaDigito(limpo, PESOS_CNPJ_SEGUNDO_DIGITO)
    );
};

// Aceita o tipo como está no formulário ("CPF"/"CNPJ") ou como vai para a API ("F"/"J").
export const nomeTipoDocumento = (tipo: string) =>
    tipo === "CPF" || tipo === "F" ? "CPF" : tipo === "CNPJ" || tipo === "J" ? "CNPJ" : "";

export const isDocumentoValido = (tipo: string, documento: string) => {
    const nome = nomeTipoDocumento(tipo);
    return nome === "CPF" ? isCpfValido(documento) : nome === "CNPJ" ? isCnpjValido(documento) : false;
};
